import {
  useCreateExtractionModelFromFile,
  useCreateNode,
  useDeleteNode,
  useDocument,
  useNode,
  useReparentNode,
  useStructuredWorkflow,
  useUpdateExtractionModelFile,
  useUpdateNode,
  useWorkflow,
} from "@/api/workflow-hooks";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Button } from "@/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { DocumentViewer } from "@/components/document-viewer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import { EditableInput } from "@/components/editable-input";
import { LoadingSpinner } from "@/components/loader";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/resizable";
import { MultipleContainers } from "@/components/sortable/multiple-containers";
import { Sortable } from "@/components/sortable/sortable";
import { Switch } from "@/components/switch";
import { usePrevious } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import TextEditor from "@/pages/Projects/Editor/TextEditor";
import CreateRuleGroupForm from "@/pages/Projects/Editor/create-rule-group-form";
import ExtractionTypeSelect from "@/pages/Projects/Editor/extraction-type-select";
import {
  Pencil1Icon,
  PersonIcon,
  PlusIcon,
  TableIcon,
  TextIcon,
  TrashIcon,
  UploadIcon,
} from "@radix-ui/react-icons";
import { GearIcon } from "@radix-ui/react-icons";
import {
  type TemplatedText,
  type TiptapDoc,
  templateToString,
} from "@repo/shared/text";
import type { DocumentType } from "@repo/shared/workflow";
import {
  type DocumentNode,
  type ExtractionNode,
  FormulaCoercion,
  type GroupNode,
  type Model,
  type ModelFieldType,
  type ObjectModel,
  isDocumentNode,
  isExtractionNode,
} from "@repo/shared/workflow";
import { produce } from "immer";
import _ from "lodash";
import Papa from "papaparse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import Actions from "./Actions";
import Deployment from "./Deployment";
import type { PageState } from "./header";

const ModelLeaf: React.FC<{
  text: string;
  className?: string;
  type?: string;
}> = ({ text, className, type }) => {
  return (
    <div className={cn("py-1.5", className)}>
      <span className="mr-1">{text}</span>
      <span className="font-mono text-xs bg-secondary rounded p-1">{type}</span>
    </div>
  );
};

const ModelTree: React.FC<{
  model: ObjectModel;
  level?: number;
}> = ({ model, level = 0 }) => {
  return (
    <div className="flex flex-col pl-4">
      {model.fields.map((field) => {
        if (field.type === "array") {
          // Handle array type - show as list with nested model
          return (
            <div className="" key={field.name}>
              <p className="py-1.5">{field.name}</p>
              <div className="">
                {field.model.type === "object" ? (
                  <div className="border-l">
                    <ModelTree model={field.model} level={level + 1} />
                  </div>
                ) : (
                  <ModelLeaf
                    text={field.model.name}
                    type={field.model.type}
                    className="border-l"
                  />
                )}
              </div>
            </div>
          );
        }

        if (field.type === "object") {
          // Handle nested object type
          return (
            <div className="" key={field.name}>
              <p className="py-1.5 font-bold">{field.name}</p>
              {field.fields.length > 0 ? (
                <ModelTree model={field} level={level + 1} />
              ) : (
                <div className="pl-4 italic text-secondary-foreground">
                  No fields
                </div>
              )}
            </div>
          );
        }

        // Handle primitive types
        return (
          <ModelLeaf
            key={field.name}
            text={field.name}
            type={field.type}
            className={field.type === "formula" ? "text-blue-600" : ""}
          />
        );
      })}
    </div>
  );
};

const WorkflowNodeEditor: React.FC = () => {
  const { id } = useParams();
  const workflowId = id as string;

  const pathParams = location.pathname.split("/").filter((p) => p !== "");
  const [_projects, _projectId, subPath, subPathId] = pathParams;
  const pageState = subPath as PageState;

  const panelRef = useRef<HTMLDivElement>(null);

  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);
  const [isModelSettingsDialogOpen, setIsModelSettingsDialogOpen] =
    useState(false);

  const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>(
    undefined,
  );

  const { data: workflowBody } = useWorkflow(workflowId);
  const workflow = workflowBody?.workflow;
  const createNodeMut = useCreateNode(workflowId);
  const deleteNodeMut = useDeleteNode(workflowId);
  const updateNodeMut = useUpdateNode(workflowId);

  const createExtractionModelFromFileMut =
    useCreateExtractionModelFromFile(workflowId);
  const updateExtractionModelFileMut = useUpdateExtractionModelFile(workflowId);

  const {
    documentNodes,
    ruleGroupHolderNodeId,
    documentGroupNodeId,
    actionGroupNodeId,
    actionNodes,
    ruleGroupNodes,
    aggregateModel: workflowModel,
  } = useStructuredWorkflow(workflowId);

  const { data: ruleGroupHolderNode } = useNode(
    workflowId,
    ruleGroupHolderNodeId,
  );

  const { data: documentGroupNode } = useNode(workflowId, documentGroupNodeId);

  const handleSelectEntity = useCallback(
    (id: string) => {
      const node = documentNodes?.find((en) => en.id === id);
      if (!node) return;

      setSelectedEntityId(node.id);
    },
    [documentNodes],
  );

  useEffect(() => {
    const firstEntityNode = documentNodes?.[0];
    if (firstEntityNode && !selectedEntityId) {
      handleSelectEntity(firstEntityNode.id);
    }
  }, [documentNodes, handleSelectEntity, selectedEntityId]);

  const selectedDoc = useMemo(
    () =>
      documentNodes?.find((m) => m.id === selectedEntityId) as
        | DocumentNode
        | undefined,
    [documentNodes, selectedEntityId],
  );

  const handleRenameDocumentNode = useCallback(
    (id: string, value: string) => {
      const docNode = documentNodes?.find((n) => n.id === id);
      if (!docNode) return;

      const newDocNode = produce(docNode, (draft) => {
        draft.name = value;
        draft.model.name = value;
      });

      updateNodeMut.mutateAsync({
        nodeId: docNode.id,
        update: newDocNode,
      });
    },
    [documentNodes, updateNodeMut],
  );

  const panelTitle =
    pageState === "extraction"
      ? "Extraction"
      : pageState === "rules"
        ? "Rules"
        : pageState === "actions"
          ? "Actions"
          : "Deployment";

  return (
    <div className="flex flex-col w-full bg-background  overflow-hidden">
      <main className="h-[calc(100dvh-4rem)]">
        <ResizablePanelGroup
          direction="horizontal"
          className="w-full sh-[calc(100dvh-8rem)]"
        >
          {pageState !== "deployment" && (
            <ResizablePanel defaultSize={18} className="flex flex-col">
              <div className="grid grid-rows-[32px,1fr,32px,1fr] h-full overflow-hidden">
                <div className="border-b bg-secondary/50 py-2 pl-4 pr-1 flex items-center justify-between">
                  <h3 className="font-medium">Documents</h3>
                  <Button
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          createExtractionModelFromFileMut.mutateAsync({
                            file,
                          });
                        }
                      };
                      input.click();
                    }}
                    size="sm"
                  >
                    <UploadIcon className="mr-2 w-4" />
                    Upload
                  </Button>
                </div>
                <div className="px-2 flex h-fit flex-col gap-1 py-2 overflow-auto">
                  <Sortable
                    items={documentNodes ?? []}
                    handle
                    Container={React.Fragment}
                    onReorder={(docs) => {
                      if (!documentGroupNode) return;

                      const newDocGroupNode = produce(
                        documentGroupNode,
                        (draft) => {
                          draft.dependencyIds = docs.map((m) => m.id);
                        },
                      );

                      updateNodeMut.mutateAsync({
                        nodeId: documentGroupNode.id,
                        update: newDocGroupNode,
                      });
                    }}
                    renderItem={(entityNode) => {
                      return (
                        <div className="flex w-full items-center justify-between group">
                          <EditableInput
                            key={entityNode.id}
                            value={entityNode.name}
                            onChange={(value) => {
                              handleRenameDocumentNode(
                                entityNode.id,
                                value as string,
                              );
                            }}
                            className={cn(
                              "px-2.5 w-full group py-1.5 cursor-pointer flex items-center justify-between",
                              {
                                "bg-accent": selectedEntityId === entityNode.id,
                                "border-transparent":
                                  selectedEntityId !== entityNode.id,
                              },
                            )}
                            editAction="double-click"
                            onClick={() => handleSelectEntity(entityNode.id)}
                          />
                          <Button
                            variant="ghost"
                            className="group-hover:opacity-100 opacity-0 mr-2"
                            size="icon"
                            onClick={() => {
                              deleteNodeMut.mutateAsync({
                                nodeId: entityNode.id,
                              });
                            }}
                          >
                            <TrashIcon className="" />
                          </Button>
                        </div>
                      );
                    }}
                  />
                </div>
                <div className="border-y bg-secondary/50 py-2 px-4">
                  <h3 className="font-medium">Variables</h3>
                </div>
                <div className="pb-4 overflow-auto flex flex-col">
                  <div className="divide-y">
                    <ModelTree model={workflowModel} />
                  </div>
                </div>
              </div>
            </ResizablePanel>
          )}
          <ResizableHandle />
          {(pageState === "rules" || pageState === "extraction") && (
            <>
              <ResizablePanel defaultSize={40}>
                {selectedDoc && (
                  <div className="h-full">
                    <div className="px-4 py-2 h-14 flex items-center justify-between">
                      <h4 className="font-medium">
                        <EditableInput
                          value={selectedDoc.name}
                          onChange={(value) =>
                            handleRenameDocumentNode(
                              selectedDoc.id,
                              value as string,
                            )
                          }
                          selectAllOnEdit
                        />
                      </h4>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsModelSettingsDialogOpen(true)}
                      >
                        <GearIcon className="w-4 h-4" />
                      </Button>
                    </div>
                    {selectedDoc?.exampleDocumentId ? (
                      <div className="px-4 overflow-auto h-[calc(100%-3.5rem)]">
                        <WorkflowDocumentViewer
                          workflowId={workflowId}
                          documentId={selectedDoc.exampleDocumentId}
                        />
                      </div>
                    ) : (
                      <div className="flex gap-2 flex-col items-center justify-center h-full text-muted-foreground">
                        <p>No example document uploaded</p>
                        <Button
                          variant="default"
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement)
                                .files?.[0];
                              if (file) {
                                updateExtractionModelFileMut.mutateAsync({
                                  nodeId: selectedDoc.id,
                                  file,
                                });
                              }
                            };
                            input.click();
                          }}
                        >
                          <UploadIcon className="mr-2 w-4" />
                          Upload document
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel>
                <div className="h-[calc(100dvh-4rem)]" ref={panelRef}>
                  <div className="border-b py-2 px-4 flex items-center justify-between sticky top-0 bg-background z-10 h-14">
                    <h3 className="font-medium py-2">{panelTitle}</h3>
                    {pageState === "rules" && (
                      <Button
                        onClick={() => setIsCreateGroupDialogOpen(true)}
                        variant="ghost"
                        className="ml-2 pl-2.5"
                      >
                        <PlusIcon className="mr-2" />
                        Group
                      </Button>
                    )}
                  </div>
                  {selectedDoc?.id != null && pageState === "extraction" && (
                    // Start of Selection
                    // 3.5rem is height of sticky sub section header
                    <div className="h-[calc(100%-3.5rem)] overflow-auto">
                      <Extraction
                        workflowId={workflowId}
                        nodeId={selectedDoc.id}
                      />
                    </div>
                  )}
                  {pageState === "rules" &&
                    !!workflow &&
                    documentGroupNode &&
                    ruleGroupHolderNode && (
                      <Rules
                        workflowId={workflowId}
                        ruleGroupNodes={ruleGroupNodes ?? []}
                        documentGroupNode={documentGroupNode as GroupNode}
                        ruleGroupHolderNode={ruleGroupHolderNode as GroupNode}
                        aggregateModel={workflowModel}
                      />
                    )}
                </div>
              </ResizablePanel>
            </>
          )}
          {pageState === "actions" && actionGroupNodeId && (
            <ResizablePanel defaultSize={82}>
              <Actions
                workflowId={workflowId}
                selectedActionId={subPathId}
                actionNodes={actionNodes ?? []}
                actionGroupNodeId={actionGroupNodeId}
              />
            </ResizablePanel>
          )}
          {pageState === "deployment" && <Deployment workflowId={workflowId} />}
        </ResizablePanelGroup>
      </main>
      <Dialog
        open={isCreateGroupDialogOpen}
        onOpenChange={setIsCreateGroupDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create group</DialogTitle>
          </DialogHeader>
          <CreateRuleGroupForm
            onSubmit={async (group) => {
              if (!ruleGroupHolderNode) {
                console.error("Could not find rule group holder");
                return;
              }

              await createNodeMut.mutateAsync({
                groupId: ruleGroupHolderNode.id,
                data: {
                  id: crypto.randomUUID(),
                  name: group.name,
                  type: "group",
                  subType: "v1",
                  dependencyIds: [],
                },
              });

              setIsCreateGroupDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isModelSettingsDialogOpen}
        onOpenChange={setIsModelSettingsDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Model Settings</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <>
              <div className="flex items-center justify-between">
                <p>Upload required</p>
                <Switch
                  checked={selectedDoc?.required}
                  onCheckedChange={() => {
                    const newDocNode = produce(selectedDoc, (draft) => {
                      draft.required = !selectedDoc.required;
                    });

                    updateNodeMut.mutateAsync({
                      nodeId: selectedDoc.id,
                      update: newDocNode,
                    });
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <p>Skip rules</p>
                <Switch
                  checked={selectedDoc?.skipIngestion}
                  onCheckedChange={() => {
                    const newDocNode = produce(selectedDoc, (draft) => {
                      draft.skipIngestion = !selectedDoc.skipIngestion;
                    });

                    updateNodeMut.mutateAsync({
                      nodeId: selectedDoc.id,
                      update: newDocNode,
                    });
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <p>Accept multiple documents</p>
                <Switch
                  checked={selectedDoc?.acceptMultiple}
                  onCheckedChange={() => {
                    const newDocNode = produce(selectedDoc, (draft) => {
                      draft.acceptMultiple = !selectedDoc.acceptMultiple;
                    });

                    updateNodeMut.mutateAsync({
                      nodeId: selectedDoc.id,
                      update: newDocNode,
                    });
                  }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Rule: React.FC<{
  workflowId: string;
  nodeId: string;
  groupId: string;
  extractions: Set<string>;
}> = ({ workflowId, nodeId, groupId, extractions }) => {
  const { data: node } = useNode(workflowId, nodeId);
  const updateNodeMut = useUpdateNode(workflowId);
  const deleteNodeMut = useDeleteNode(workflowId);

  const { textBody, ruleType } = useMemo(() => {
    if (!node || !isExtractionNode(node)) {
      console.log("Invalid node", node);
      return { textBody: "", ruleType: "Prompt" };
    }

    let textBody: TemplatedText = "";
    let ruleType = "Prompt";

    // Prompt or user rules
    if (node.model.type === "object") {
      const resultField = node.model.fields.find((f) => f.name === "result");
      ruleType = node.model.userChoice ? "User" : "Prompt";
      textBody = resultField?.description ?? "";
    } else if (node.model.type === "formula") {
      // Formula rules
      textBody = node.model.formula ?? "";
      ruleType = "Formula";
    }

    return {
      textBody,
      ruleType,
    };
  }, [node]);

  const handleUpdateNodeValue = useCallback(
    (value: TiptapDoc) => {
      if (!node) return;

      const newNode = produce(node, (draft) => {
        if (!isExtractionNode(draft)) {
          throw new Error("Invalid node");
        }

        if (draft.model.type === "object") {
          const resultField = draft.model.fields.find(
            (f) => f.name === "result",
          );
          if (!resultField) {
            throw new Error("Result field not found");
          }

          resultField.description = value;
        } else if (draft.model.type === "formula") {
          draft.model.formula = value;
        }
      });

      updateNodeMut.mutateAsync({
        nodeId: newNode.id,
        update: newNode,
      });
    },
    [node, updateNodeMut],
  );

  const debouncedUpdateNodeValue = useDebouncedCallback(
    handleUpdateNodeValue,
    500,
  );

  if (!node || !isExtractionNode(node)) {
    console.log("Invalid node", node);
    return <div>Invalid node.</div>;
  }

  return (
    <div className="flex flex-col w-full pt-1 pb-2">
      {/* <pre>{JSON.stringify(node)}</pre> */}
      <div className="group overflow-hidden grid grid-cols-[1fr,80px,30px] gap-1.5 items-center">
        <EditableInput
          value={node.name}
          onChange={(value) => {
            const newNode = produce(node, (draft) => {
              draft.name = value as string;
              draft.model.name = draft.name;
            });

            updateNodeMut.mutateAsync({
              nodeId: node.id,
              update: newNode,
            });
          }}
          selectAllOnEdit
          className="ml-0 pl-2 truncate font-medium"
        />
        <div className="select-none">
          <span className="px-2 capitalize py-1 bg-secondary rounded-full text-xs">
            {ruleType}
          </span>
        </div>
        <Button
          variant="ghost"
          className="group-hover:opacity-100 opacity-0"
          size="icon"
          onClick={() => {
            deleteNodeMut.mutateAsync({
              nodeId: node.id,
              groupId,
            });
            // deleteRuleMut.mutateAsync(rule.rule_id)
          }}
        >
          <TrashIcon className="" />
        </Button>
      </div>
      <TextEditor
        content={textBody}
        onChange={(v) => debouncedUpdateNodeValue(v)}
        placeholder={"hi"}
        className="p-2 truncate rounded-md"
        extractions={extractions}
      />
    </div>
  );
};

const Rules: React.FC<{
  workflowId: string;
  ruleGroupNodes: GroupNode[];
  documentGroupNode: GroupNode;
  ruleGroupHolderNode: GroupNode;
  aggregateModel: Model;
}> = ({
  workflowId,
  ruleGroupNodes: groups,
  documentGroupNode,
  ruleGroupHolderNode,
  aggregateModel,
}) => {
  const deleteNodeMut = useDeleteNode(workflowId);
  const createNodeMut = useCreateNode(workflowId);
  const updateNodeMut = useUpdateNode(workflowId);
  const reparentNodeMut = useReparentNode(workflowId);

  const sortableGroups = useMemo(() => {
    const ret: Record<string, { id: string }[]> = {};
    for (const groupId of ruleGroupHolderNode.dependencyIds) {
      const group = groups?.find((g) => g.id === groupId);
      if (group) {
        ret[groupId] = group?.dependencyIds.map((id) => ({ id }));
      }
    }

    return ret;
  }, [groups, ruleGroupHolderNode]);

  const prevGroupsLength = usePrevious(groups?.length);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to the bottom if a new rule group was added
    if (groups && prevGroupsLength && groups.length > prevGroupsLength) {
      setTimeout(() => {
        panelRef.current?.scrollBy({
          top: panelRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [groups, prevGroupsLength]);

  const variableStrings = useMemo(() => {
    const ret = new Set<string>();
    if (aggregateModel.type !== "object") {
      return ret;
    }

    for (const field of aggregateModel.fields ?? []) {
      if (field.type === "object") {
        for (const nestedField of field.fields ?? []) {
          ret.add(`${field.name}.${nestedField.name}`);
        }
      }
    }

    // for (const variable of aggregateModel.fields ?? []) {
    //   extractionsWorking.add(`${aggregateModel.name}.${variable.name}`);
    // }
    return ret;
  }, [aggregateModel]);

  return (
    <div
      className="h-[calc(100dvh-7.5rem)] flex overflow-auto flex-col divide-y"
      ref={panelRef}
    >
      {sortableGroups && (
        <MultipleContainers
          items={sortableGroups}
          vertical
          handle
          onRemove={(containerId) => {
            // The backend should delete all children of the group
            deleteNodeMut.mutateAsync({
              nodeId: containerId.toString(),
            });
          }}
          renderItem={({ id: nodeId }) => {
            const parentGroup = groups?.find((g) =>
              g.dependencyIds.includes(nodeId),
            );

            if (!parentGroup) {
              console.log("Parent group not found", nodeId);
              return <div>Invalid rule</div>;
            }

            return (
              <Rule
                workflowId={workflowId}
                nodeId={nodeId}
                groupId={parentGroup.id}
                extractions={variableStrings}
              />
            );
          }}
          renderContainerLabel={(id) => {
            const group = groups?.find((g) => g.id === id);
            if (!group) return <p>Untitled group</p>;

            return (
              <EditableInput
                value={group.name || ""}
                onClick={() => {
                  // TODO: collapse the container
                }}
                onChange={(value) => {
                  const newGroup = produce(group, (draft) => {
                    draft.name = value as string;
                  });

                  updateNodeMut.mutateAsync({
                    nodeId: group.id,
                    update: newGroup,
                  });
                }}
                selectAllOnEdit
                className="font-semibold"
              />
            );
          }}
          renderCreateItem={(containerId) => {
            const selectedRuleGroup = groups?.find((g) => g.id === containerId);
            const existingRuleCount =
              selectedRuleGroup?.dependencyIds.length ?? 0;

            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="link" className="w-fit">
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Rule
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-96" align="start">
                  <DropdownMenuItem
                    className="flex flex-col w-full items-start"
                    onSelect={() => {
                      if (!selectedRuleGroup) {
                        throw new Error("Selected group not found");
                      }

                      const name = `Prompt rule ${existingRuleCount + 1}`;
                      const id = crypto.randomUUID();

                      // NOTE(yogi): While we're using this editor, we will just make two separate
                      // redundant calls to create the node and update the group its in
                      // Once we switch to the DAG editor, we can make a single call
                      createNodeMut.mutateAsync({
                        groupId: selectedRuleGroup.id,
                        data: {
                          id,
                          name,
                          type: "transform",
                          subType: "extraction",
                          showDescription: true,
                          dependencyIds: [documentGroupNode.id],
                          model: {
                            type: "object",
                            name,
                            fields: [
                              {
                                name: "result",
                                type: "enum",
                                description: "",
                                types: ["Passed", "Failed", "Not Applicable"],
                              },
                              {
                                name: "reasoning",
                                type: "string",
                                description: "Reasoning for the result",
                              },
                            ],
                          },
                        },
                      });

                      const updatedGroup = structuredClone(
                        selectedRuleGroup,
                      ) as GroupNode;
                      updatedGroup.dependencyIds.push(id);

                      updateNodeMut.mutateAsync({
                        nodeId: selectedRuleGroup.id,
                        update: updatedGroup,
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <TextIcon className="mr-2 h-4 w-4" />
                      <span>Prompt Rule</span>
                    </div>
                    <p className="ml-6 mb-1 mt-0.5 text-sm text-gray-600 p-0 font-normal">
                      Used to extract a string of text or numbers, such as
                      address, account balance, or filing status
                    </p>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="flex flex-col w-full items-start"
                    onSelect={() => {
                      if (!selectedRuleGroup) {
                        throw new Error("Selected group not found");
                      }

                      const name = `Formula rule ${existingRuleCount + 1}`;
                      const id = crypto.randomUUID();

                      createNodeMut.mutateAsync({
                        groupId: selectedRuleGroup.id,
                        data: {
                          id,
                          name,
                          type: "transform",
                          subType: "extraction",
                          showDescription: true,
                          dependencyIds: [documentGroupNode.id],
                          model: {
                            type: "formula",
                            name,
                            formula: "",
                            coerce: FormulaCoercion.PASS_FAIL_NA,
                          },
                        },
                      });

                      const updatedGroup = structuredClone(
                        selectedRuleGroup,
                      ) as GroupNode;
                      updatedGroup.dependencyIds.push(id);

                      updateNodeMut.mutateAsync({
                        nodeId: selectedRuleGroup.id,
                        update: updatedGroup,
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <TableIcon className="mr-2 h-4 w-4" />
                      <span>Formula Rule</span>
                    </div>
                    <p className="ml-6 mb-1 mt-0.5 text-sm text-gray-600 p-0 font-normal">
                      Use a formula to calculate a value, such as a total,
                      average, or percentage
                    </p>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="flex flex-col w-full items-start"
                    onSelect={() => {
                      if (!selectedRuleGroup) {
                        throw new Error("Selected group not found");
                      }

                      const name = `User Approval Rule ${existingRuleCount + 1}`;
                      const id = crypto.randomUUID();

                      createNodeMut.mutateAsync({
                        groupId: selectedRuleGroup.id,
                        data: {
                          id,
                          name,
                          type: "transform",
                          subType: "extraction",
                          showDescription: true,
                          dependencyIds: [documentGroupNode.id],
                          model: {
                            type: "object",
                            name,
                            userChoice: true,
                            fields: [
                              {
                                name: "result",
                                type: "enum",
                                description: "",
                                types: ["Passed", "Failed", "Not Applicable"],
                              },
                              {
                                name: "reasoning",
                                type: "string",
                                description: "Reasoning for the result",
                              },
                            ],
                          },
                        },
                      });

                      const updatedGroup = structuredClone(
                        selectedRuleGroup,
                      ) as GroupNode;
                      updatedGroup.dependencyIds.push(id);

                      updateNodeMut.mutateAsync({
                        nodeId: selectedRuleGroup.id,
                        update: updatedGroup,
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <PersonIcon className="mr-2 h-4 w-4" />
                      <span>User Approval Rule</span>
                    </div>
                    <p className="ml-6 mb-1 mt-0.5 text-sm text-gray-600 p-0 font-normal">
                      Ask a human to review and approve the result
                    </p>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          }}
          onReparent={(childId, oldParentId, newParentId) => {
            reparentNodeMut.mutateAsync({
              nodeId: childId.toString(),
              oldGroupId: oldParentId.toString(),
              newGroupId: newParentId.toString(),
            });
          }}
          onReorderContainer={(containerIds) => {
            const newRuleGroupHolderNode = structuredClone(
              ruleGroupHolderNode,
            ) as GroupNode;
            newRuleGroupHolderNode.dependencyIds = containerIds.map((id) =>
              id.toString(),
            );

            updateNodeMut.mutateAsync({
              nodeId: ruleGroupHolderNode.id,
              update: newRuleGroupHolderNode,
            });
          }}
          onReorderChild={(containerId, ids) => {
            const newGroup = structuredClone(
              groups?.find((g) => g.id === containerId),
            ) as GroupNode;
            newGroup.dependencyIds = ids.map((id) => id.toString());

            updateNodeMut.mutateAsync({
              nodeId: containerId.toString(),
              update: newGroup,
            });
          }}
        />
      )}
    </div>
  );
};

const getRelevantExtractionModel = (
  model: Model | null | undefined,
  path: string[],
) => {
  if (!model) {
    return null;
  }

  if (model.type !== "object") return null;

  if (path.length === 0) {
    return model as ObjectModel;
  }

  const nestedKey = path[0];
  const nestedModel = model.fields.find((f) => f.name === nestedKey);
  if (
    nestedKey &&
    nestedModel &&
    nestedModel.type === "array" &&
    nestedModel.model.type === "object"
  ) {
    return nestedModel.model as ObjectModel;
  }

  return null;
};

const generateUniqueFieldName = (fields: Model[]) => {
  const numFields = fields.length;
  const numFieldsWithNewName = fields.filter(
    (f) => f.name === `Field ${numFields + 1}`,
  ).length;
  const uniqueFieldName = `Field ${numFields + numFieldsWithNewName + 1}`;

  return uniqueFieldName;
};

const Extraction: React.FC<{
  workflowId: string;
  nodeId: string;
}> = ({ workflowId, nodeId }) => {
  const [path, setPath] = useState<string[]>([]);
  const { data: node, isLoading } = useNode(workflowId, nodeId);
  const updateNodeMut = useUpdateNode(workflowId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset path when nodeId changes
  useEffect(() => {
    setPath([]);
  }, [nodeId]);

  const fields = useMemo(() => {
    // in this editor only consider document extraction models
    if (!node || !isDocumentNode(node)) return null;

    return getRelevantExtractionModel(node.model, path)?.fields;
  }, [node, path]);

  const handleUpdateNode = useCallback(
    (mutator: (model: ObjectModel) => void) => {
      if (!node) return;

      // TODO: more performant way to do this (?)
      const newNode = structuredClone(node) as ExtractionNode;
      const relevantModel = getRelevantExtractionModel(newNode.model, path);
      if (!relevantModel) {
        console.log("Could not find the type of the object model");
        return;
      }

      mutator(relevantModel);

      updateNodeMut.mutateAsync({
        nodeId: node.id,
        update: newNode,
      });
    },
    [node, path, updateNodeMut],
  );

  return (
    <div className="">
      {/* <pre>{JSON.stringify(fields, null, 2)}</pre> */}
      <Breadcrumb className="px-4 py-2">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={() => {
                setPath([]);
              }}
            >
              Base Fields
            </BreadcrumbLink>
          </BreadcrumbItem>
          {path.map((field) => {
            const name = field;

            return (
              <React.Fragment key={name}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    className={cn({
                      "cursor-default hover:text-unset":
                        path[path.length - 1] === field,
                    })}
                    onClick={() => {
                      if (path[path.length - 1] === field) return;

                      setPath((prev) => prev.slice(0, prev.indexOf(field)));
                    }}
                  >
                    {name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="bg-secondary/50 pt-3 pb-2 font-medium border-y text-sm text-gray-500 grid px-4 grid-cols-[140px,80px,1fr]">
        <div className="">Name</div>
        <div className="">Type</div>
        <div className="text-sm px-4">Description</div>
      </div>
      <div className="flex flex-col divide-y">
        {isLoading || !node || !fields ? (
          <div className="flex items-center justify-center h-16">
            <LoadingSpinner size="small" />
          </div>
        ) : (
          fields.map((field) => (
            <div
              key={field.name}
              className="group flex items-center justify-between pl-4 pr-2 py-0"
            >
              <div className="grid py-1.5 grid-cols-[140px,80px,1fr,100px] items-center w-full">
                <div className="">
                  <EditableInput
                    value={field.name ?? ""}
                    onChange={(value) => {
                      handleUpdateNode((model) => {
                        for (const f of model.fields) {
                          if (f.name === field.name) {
                            if (
                              f.type === "array" &&
                              f.model.type === "object"
                            ) {
                              // For list models, also rename the inner model type
                              f.model.name = (value as string).trim();
                            }

                            f.name = (value as string).trim();
                            break;
                          }
                        }
                      });
                    }}
                    selectAllOnEdit
                  />
                </div>
                <div className="text-sm text-gray-500">
                  {field.type === "array" ? (
                    <div className="pl-4">List</div>
                  ) : (
                    <ExtractionTypeSelect
                      value={field.type}
                      onChange={(value) => {
                        handleUpdateNode((model) => {
                          for (const f of model.fields) {
                            if (f.name === field.name) {
                              f.type = value as ModelFieldType;
                              break;
                            }
                          }
                        });
                      }}
                    />
                  )}
                </div>
                <div className="text-sm px-4 truncate overflow-hidden">
                  <EditableInput
                    value={templateToString(field.description || "")}
                    onChange={(value) => {
                      handleUpdateNode((model) => {
                        for (const f of model.fields) {
                          if (f.name === field.name) {
                            f.description = value as string;
                            break;
                          }
                        }
                      });
                    }}
                  />
                </div>
                <div className="flex items-center justify-end gap-1">
                  {field.type === "array" ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setPath((prev) => [...prev, field.name]);
                      }}
                      className="shrink-0"
                      size="sm"
                    >
                      Edit <Pencil1Icon className="ml-2 shrink-0" />
                    </Button>
                  ) : (
                    <div />
                  )}
                  <Button
                    variant="ghost"
                    className="py-0 group-hover:opacity-100 opacity-0"
                    onClick={() => {
                      handleUpdateNode((model) => {
                        const index = model.fields.findIndex(
                          (f) => f.name === field.name,
                        );

                        if (index !== -1) {
                          model.fields.splice(index, 1);
                        }
                      });
                    }}
                    size="icon"
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {!isLoading && fields && Object.keys(fields).length === 0 && (
        <div className="flex items-center justify-center h-16">
          <p className="text-sm text-gray-500">No fields</p>
        </div>
      )}
      {!isLoading && (
        <div className="">
          <Button
            onClick={() => {
              handleUpdateNode((model) => {
                const uniqueFieldName = generateUniqueFieldName(model.fields);
                model.fields.push({
                  type: "string",
                  name: uniqueFieldName,
                  description: "",
                });
              });
            }}
            variant="ghost"
            className="mt-2 ml-2 px-2.5"
          >
            <PlusIcon className="mr-2" />
            Field
          </Button>
          {path.length === 0 && (
            <Button
              onClick={() => {
                handleUpdateNode((model) => {
                  const uniqueFieldName = generateUniqueFieldName(model.fields);

                  model.fields.push({
                    type: "array",
                    name: uniqueFieldName,
                    description: "",
                    model: {
                      type: "object",
                      name: uniqueFieldName,
                      fields: [],
                    },
                  });
                });
              }}
              variant="ghost"
              className="mt-2 ml-2 px-2.5"
            >
              <PlusIcon className="mr-2" />
              List
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const WorkflowDocumentViewer: React.FC<{
  workflowId: string;
  documentId: string;
}> = ({ workflowId, documentId }) => {
  const { data } = useDocument(workflowId, documentId);
  const signedUrl = data?.signedUrl;

  if (!signedUrl) {
    return null;
  }

  if (!data?.document.fileType) {
    return null;
  }

  return (
    <DocumentViewer
      documentType={data.document.fileType as DocumentType}
      signedUrl={signedUrl}
    />
  );
};

export default WorkflowNodeEditor;
